/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  Sessions data
  ============================================================

  Tämä rakenne jäljittelee tulevaa WordPress REST API -dataa.

  Etusivun SessionsSection käyttää edelleen:
  - id
  - translationKey
  - image

  Varsinainen SessionsPage käyttää lisäksi:
  - slug
  - enabled
  - order
  - content FI / EN
*/

export const sessionsPageContent = {
  hero: {
    eyebrow: {
      fi: "Kuvaukset · Palvelut",
      en: "Sessions · Services",
    },

    title: {
      fi: "Kuvauksen ei tarvitse tuntua kuvaukselta.",
      en: "A session does not have to feel like a session.",
    },

    description: {
      fi:
        "Tärkeintä ei ole täydellinen poseeraus vaan se, miltä hetki tuntui. Kuvaus suunnitellaan teidän, päivän ja tarinan ympärille.",
      en:
        "The most important thing is not a perfect pose, but how the moment felt. Every session is shaped around you, the day and the story.",
    },

    indexLabel: {
      fi: "Valitse kuvaus",
      en: "Choose a session",
    },
  },

  intro: {
    eyebrow: {
      fi: "Lähtökohta",
      en: "The starting point",
    },

    title: {
      fi: "Kuvat saavat näyttää teiltä.",
      en: "Your photographs should feel like you.",
    },

    text: {
      fi:
        "Jokainen kuvaus alkaa ajatuksesta siitä, mitä haluatte muistaa. Paikka, valo, tunnelma ja rytmi rakentuvat sen ympärille. Valmiin kaavan sijaan tarkoitus on tehdä tilaa oikeille hetkille.",
      en:
        "Every session begins with what you want to remember. Location, light, atmosphere and pace are built around that. Instead of following a fixed formula, the aim is to make room for real moments.",
    },
  },

  process: {
    eyebrow: {
      fi: "Näin kuvaus etenee",
      en: "How it works",
    },

    title: {
      fi: "Rauhallinen prosessi alusta loppuun.",
      en: "A calm process from start to finish.",
    },

    items: [
      {
        id: "01",

        title: {
          fi: "Suunnittelu",
          en: "Planning",
        },

        text: {
          fi:
            "Käymme ennen kuvausta läpi toiveet, tunnelman, paikan ja käytännön yksityiskohdat.",
          en:
            "Before the session we go through your wishes, atmosphere, location and practical details.",
        },
      },

      {
        id: "02",

        title: {
          fi: "Kuvaus",
          en: "The session",
        },

        text: {
          fi:
            "Kuvauksessa ei tarvitse osata esiintyä. Ohjaan silloin kun sitä tarvitaan ja annan tilanteiden tapahtua silloin kun se toimii paremmin.",
          en:
            "You do not need to know how to pose. I guide when guidance helps and let moments unfold naturally when that works better.",
        },
      },

      {
        id: "03",

        title: {
          fi: "Valmiit kuvat",
          en: "Final photographs",
        },

        text: {
          fi:
            "Kuvat viimeistellään yhtenäiseksi kokonaisuudeksi ja toimitetaan sovitulla tavalla valmiina säilytettäviksi ja jaettaviksi.",
          en:
            "The photographs are carefully finished as one cohesive collection and delivered ready to keep and share.",
        },
      },
    ],
  },

  cta: {
    eyebrow: {
      fi: "Onko mielessä jotain muuta?",
      en: "Something else in mind?",
    },

    title: {
      fi: "Kerro, millaisia kuvia tarvitset.",
      en: "Tell me what you would like to create.",
    },

    text: {
      fi:
        "Kaikki kuvaukset eivät mahdu valmiiseen kategoriaan. Kerro ajatuksestasi, niin suunnitellaan siihen sopiva kokonaisuus.",
      en:
        "Not every idea fits neatly into a category. Tell me what you have in mind and we can shape the right session around it.",
    },

    button: {
      fi: "Ota yhteyttä",
      en: "Get in touch",
    },
  },
};

const sessionItems = [
  {
    id: "01",

    slug: "weddings",

    translationKey: "weddings",

    enabled: true,
    featured: true,

    order: 10,

    image:
      "/media/portfolio/wedding-04.webp",

    content: {
      fi: {
        eyebrow:
          "Häät",

        title:
          "Hääkuvaus",

        description:
          "Päivästä ei tarvitse rakentaa kuvia varten mitään ylimääräistä. Taltioin tunnelman, ihmiset ja pienet hetket osaksi yhtä tarinaa.",

        lead:
          "Hääpäivä kulkee nopeasti. Kuvien tehtävä on hidastaa se myöhemmin takaisin hetkiksi.",

        alt:
          "Hääkuvaus",

        details: [
          "Dokumentaarinen ja rauhallinen kuvaustapa",
          "Päivän kulkuun sopiva kokonaisuus",
          "Potretit ja aidot tilanteet saman tarinan sisällä",
        ],
      },

      en: {
        eyebrow:
          "Weddings",

        title:
          "Wedding photography",

        description:
          "Nothing extra needs to be created for the photographs. I document the atmosphere, people and small moments as one complete story.",

        lead:
          "A wedding day moves quickly. Photographs allow you to slow it back down into individual moments later.",

        alt:
          "Wedding photography",

        details: [
          "Calm, documentary approach",
          "Coverage shaped around the rhythm of your day",
          "Portraits and real moments within the same story",
        ],
      },
    },
  },

  {
    id: "02",

    slug: "couples",

    translationKey: "couples",

    enabled: true,
    featured: true,

    order: 20,

    image:
      "/media/portfolio/couple-04.webp",

    content: {
      fi: {
        eyebrow:
          "Parit",

        title:
          "Parikuvaus",

        description:
          "Kuvaus kahdelle ihmiselle ilman tarvetta osata poseerata. Rakennetaan tilanne, jossa huomio saa olla toisessa ihmisessä kameran sijaan.",

        lead:
          "Hyvä parikuva ei kerro vain siitä, miltä näytitte vaan siitä, millaisia olitte yhdessä.",

        alt:
          "Parikuvaus",

        details: [
          "Rento ohjaus ilman jäykkää poseerausta",
          "Miljöö valitaan tunnelman mukaan",
          "Sopii myös kihlajais- ja vuosipäiväkuvaukseen",
        ],
      },

      en: {
        eyebrow:
          "Couples",

        title:
          "Couple sessions",

        description:
          "A session for two people without any need to know how to pose. The idea is to create space where your attention can stay on each other rather than the camera.",

        lead:
          "A good photograph of two people remembers not only how you looked, but how you were together.",

        alt:
          "Couple photography",

        details: [
          "Relaxed guidance without rigid posing",
          "Location chosen around the desired atmosphere",
          "Also suitable for engagements and anniversaries",
        ],
      },
    },
  },

  {
    id: "03",

    slug: "portraits",

    translationKey: "portraits",

    enabled: true,
    featured: true,

    order: 30,

    image:
      "/media/portfolio/portrait-04.webp",

    content: {
      fi: {
        eyebrow:
          "Henkilöt",

        title:
          "Henkilökuvaus",

        description:
          "Henkilökuva voi olla hiljainen, vahva, herkkä tai jotain aivan muuta. Tärkeintä on, että lopputulos tuntuu ihmiseltä kuvassa.",

        lead:
          "Muotokuva toimii silloin, kun siinä näkyy muutakin kuin kasvot.",

        alt:
          "Henkilökuvaus",

        details: [
          "Henkilökohtainen kuvausympäristö ja tunnelma",
          "Luonteva ohjaus kuvauksen aikana",
          "Sopii yksityisiin ja ammatillisiin tarpeisiin",
        ],
      },

      en: {
        eyebrow:
          "Portraits",

        title:
          "Portrait sessions",

        description:
          "A portrait can be quiet, strong, delicate or something entirely different. What matters is that the result feels like the person in the photograph.",

        lead:
          "A portrait works when it shows more than a face.",

        alt:
          "Portrait photography",

        details: [
          "A personal setting and visual atmosphere",
          "Natural guidance throughout the session",
          "Suitable for both personal and professional use",
        ],
      },
    },
  },

  {
    id: "04",

    slug: "editorial",

    translationKey: "editorial",

    enabled: true,
    featured: false,

    order: 40,

    image:
      "/media/portfolio/editorial-03.webp",

    content: {
      fi: {
        eyebrow:
          "Editorial · Brändi",

        title:
          "Editorial- ja brändikuvaus",

        description:
          "Kun kuvilla on tehtävä, rakennetaan niille selkeä maailma. Editorial- ja brändikuvaus yhdistää ihmisen, ympäristön ja visuaalisen ilmeen harkituksi kokonaisuudeksi.",

        lead:
          "Vahva kuva ei vain näytä hyvältä. Se kertoo heti, miltä brändin pitäisi tuntua.",

        alt:
          "Editorial- ja brändikuvaus",

        details: [
          "Visuaalinen suunnittelu ennen kuvausta",
          "Henkilö-, lifestyle- ja brändikuvat samassa kokonaisuudessa",
          "Sopii verkkosivuille, kampanjoihin ja sosiaaliseen mediaan",
        ],
      },

      en: {
        eyebrow:
          "Editorial · Brand",

        title:
          "Editorial and brand photography",

        description:
          "When photographs have a purpose, we create a clear visual world around them. Editorial and brand photography brings together people, environment and identity.",

        lead:
          "A strong image does more than look good. It immediately communicates how a brand should feel.",

        alt:
          "Editorial and brand photography",

        details: [
          "Visual planning before the session",
          "Portrait, lifestyle and brand imagery within one collection",
          "Ideal for websites, campaigns and social media",
        ],
      },
    },
  },

  {
    id: "05",

    slug: "products",

    translationKey: "products",

    enabled: true,
    featured: false,

    order: 50,

    image:
      "/media/portfolio/product-02.webp",

    content: {
      fi: {
        eyebrow:
          "Tuotteet",

        title:
          "Tuotekuvaus",

        description:
          "Hyvä tuotekuva tekee materiaalin, muodon ja yksityiskohdat näkyviksi. Kuvaus voidaan rakentaa pelkistetyn tarkaksi tai kokonaisen tunnelman ympärille.",

        lead:
          "Tuote ei tarvitse ympärilleen paljon. Se tarvitsee oikean valon ja tarkoituksen.",

        alt:
          "Tuotekuvaus",

        details: [
          "Tuote- ja yksityiskohtakuvat",
          "Pelkästään tai stailattuna ympäristöön",
          "Verkkokauppa-, kampanja- ja markkinointikäyttöön",
        ],
      },

      en: {
        eyebrow:
          "Products",

        title:
          "Product photography",

        description:
          "Good product photography makes material, form and detail visible. The session can be clean and precise or built around a complete visual atmosphere.",

        lead:
          "A product does not need much around it. It needs the right light and a clear purpose.",

        alt:
          "Product photography",

        details: [
          "Product and detail photographs",
          "Clean studio imagery or styled environments",
          "For ecommerce, campaigns and marketing",
        ],
      },
    },
  },

  {
    id: "06",

    slug: "business",

    translationKey: "business",

    enabled: true,
    featured: false,

    order: 60,

    image:
      "/media/portfolio/business-01.webp",

    content: {
      fi: {
        eyebrow:
          "Yritys · Miljöö",

        title:
          "Yritys- ja miljöökuvaus",

        description:
          "Työ, tila ja ihmiset muodostavat yrityksen tarinan. Kuvauksessa tehdään näkyväksi se, mitä yrityksessä tapahtuu ja miltä sen maailma tuntuu.",

        lead:
          "Hyvät yrityskuvat näyttävät työn sellaisena, että siihen haluaa päästä lähemmäksi.",

        alt:
          "Yritys- ja miljöökuvaus",

        details: [
          "Henkilöstö- ja työskentelykuvat",
          "Toimitilat, työympäristö ja yksityiskohdat",
          "Kuvapankki verkkosivuille ja markkinointiin",
        ],
      },

      en: {
        eyebrow:
          "Business · Environment",

        title:
          "Business and location photography",

        description:
          "Work, place and people form the story of a business. The session makes visible what happens there and how the world around the brand feels.",

        lead:
          "Good business photography makes the work feel worth looking closer at.",

        alt:
          "Business and location photography",

        details: [
          "Team and working-life photography",
          "Premises, environments and details",
          "Image library for websites and marketing",
        ],
      },
    },
  },

  {
    id: "07",

    slug: "memorial",

    translationKey: "memorial",

    enabled: true,
    featured: false,

    order: 70,

    image:
      "/media/portfolio/memorial-02.webp",

    content: {
      fi: {
        eyebrow:
          "Muistotilaisuudet",

        title:
          "Muistotilaisuuden kuvaus",

        description:
          "Muistotilaisuuden kuvaamisessa tärkeintä on hienovaraisuus. Kuvaaja on läsnä huomaamattomasti ja tallentaa tilan, yksityiskohdat ja kohtaamiset arvokkaasti.",

        lead:
          "Kaikkea tärkeää ei tarvitse sanoa ääneen. Joskus yksi kuva riittää muistamaan.",

        alt:
          "Muistotilaisuuden kuvaus",

        details: [
          "Huomaamaton ja kunnioittava työskentely",
          "Tilaisuuden tunnelma ja tärkeät yksityiskohdat",
          "Kuvat läheisille säilytettäviksi",
        ],
      },

      en: {
        eyebrow:
          "Memorials",

        title:
          "Memorial photography",

        description:
          "Discretion matters most when photographing a memorial. I work quietly and document the place, details and encounters with care and respect.",

        lead:
          "Not everything important needs to be said aloud. Sometimes one photograph is enough to remember.",

        alt:
          "Memorial photography",

        details: [
          "Discreet and respectful approach",
          "Atmosphere and meaningful details",
          "Photographs created for family to keep",
        ],
      },
    },
  },

  {
    id: "08",

    slug: "seasonal",

    translationKey: "seasonal",

    enabled: true,
    featured: false,

    order: 80,

    image:
      "/media/portfolio/seasonal-04.webp",

    content: {
      fi: {
        eyebrow:
          "Sesonki",

        title:
          "Sesonkikuvaus",

        description:
          "Vuodenajat tuovat kuviin oman valonsa ja tunnelmansa. Sesonkikuvaus voi olla joulun lämmin hetki, talvinen muotokuva tai juuri tiettyyn vuodenaikaan rakennettu kokonaisuus.",

        lead:
          "Jokaisella vuodenajalla on valo, jonka tunnistaa myöhemmin yhdellä silmäyksellä.",

        alt:
          "Sesonkikuvaus",

        details: [
          "Joulu-, talvi- ja muut kausikuvaukset",
          "Henkilöille, pareille, perheille ja yrityksille",
          "Sesongin mukaan suunniteltu miljöö ja tunnelma",
        ],
      },

      en: {
        eyebrow:
          "Seasonal",

        title:
          "Seasonal sessions",

        description:
          "Every season brings its own light and atmosphere. A seasonal session might be a warm Christmas moment, a winter portrait or imagery built around a specific time of year.",

        lead:
          "Every season has a light you can recognise again with a single glance.",

        alt:
          "Seasonal photography",

        details: [
          "Christmas, winter and other seasonal sessions",
          "For individuals, couples, families and businesses",
          "Location and atmosphere designed around the season",
        ],
      },
    },
  },
];

export default sessionItems;